import axios from 'axios';
import { hydratePlayback } from '../../utils/episodePlayback';
import { followingFeedIdentity } from '../../utils/followingFeedIdentity';
import jwtDecode from 'jwt-decode';

import {
    UPDATED_SAVED_LIST,
    DELETED_SAVED_LIST,
    ADD_TRACK_TO_SAVED_LIST,
    REMOVE_TRACK_FROM_SAVED_LIST,

    ADD_SPEAKER_SUBSCRIBE_IDS,
    FOR_ME_LOADING_COUNT_INCREASE,
    FOR_ME_LOADING_COUNT_DECREASE,
    LOAD_TOPIC_PREFERENCES,
    LOAD_LATEST_SUBSCRIED,
    LATEST_SUBSCRIBED_LOAD_FAILED,
    INVALIDATE_LATEST_SUBSCRIBED,
    LOAD_RECENTLY_PLAYED,
    LOAD_RECENTLY_SAVED,
    SAVE_TRACK_MUTATION_FINISHED,
    SAVE_TRACK_MUTATION_STARTED,
    SET_AUTHENTICATED,
    SET_FOR_ME,
    SET_GET_STARTED_TOPICS_STATUS_TRUE,
    SET_LATEST_SUBSCRIED,
    SET_PODCAST_SUBSCRIBE_STATUS,
    SET_RECENTLY_PLAYED,
    SET_RECENTLY_SAVED,
    SET_SAVE_TRACK_STATUS,
    SET_SPEAKER_SUBSCRIBE_STATUS,
    SET_UNAUTHENTICATED,
    SET_USER,
    SYNC_SUBSCRIBE_STATUSES,
    SYNC_TOPIC_PREFERENCES,
    TOPIC_PREFERENCE_MUTATION_FINISHED,
    TOPIC_PREFERENCE_MUTATION_ROLLED_BACK,
    TOPIC_PREFERENCE_MUTATION_STARTED,
    TOPIC_PREFERENCES_LOAD_FAILED,
    TOPIC_PREFERENCES_LOADED,
} from '../types';

import { setFirebaseToken, validTokenExists } from '../../firebaseHelper'
import { GET_STARTED_NEW_USER_RESPONSE } from '../../constants/local_storage_constants';
import {
    normalizeSubscribeStatus,
} from '../../utils/subscribeStatus';
import { saveShowStatus, showSearchKey } from '../../utils/showSearch';
import { beginForMePreferenceSave, finishForMePreferenceSave } from './forMePreferenceActions';
import { getOnboardingPlayback, getUpcomingOnboardingHighlights, markOnboardingSignedIn, readOnboardingSession, saveOnboardingHomePicks, updateOnboardingSession } from '../../utils/onboardingSession';
import { runOnboardingSaveRequest } from '../../utils/onboardingSaveRequest';
export { loadForMePreviews, prepareForMeWithLatestPreferences, applyPreparedForMe, retryForMeRefreshIfNeeded } from './forMePreferenceActions';

let topicPreferenceRequestSequence = 0;
let topicPreferenceMutationSequence = 0;
let latestSubscribedRequestSequence = 0;
const FOLLOWING_CACHE_TTL_MS = 30 * 60 * 1000;
const FOLLOWING_RETRY_DELAY_MS = 60 * 1000;
const showPreferenceRequests = new Map();
const speakerPreferenceRequests = new Map();
const savedTrackRequests = new Map();

export const waitForShowPreferenceSaves = (uid) => Promise.allSettled(
    [...showPreferenceRequests].filter(([key]) => key.startsWith(`${uid}:`)).map(([, request]) => request),
);

export const waitForSpeakerPreferenceSaves = (uid) => Promise.allSettled(
    [...speakerPreferenceRequests].filter(([key]) => key.startsWith(`${uid}:`)).map(([, request]) => request),
);

const topicPreferenceErrorMessage = (err) => (
    err?.response?.data?.error ||
    err?.response?.data?.general ||
    err?.message ||
    'Unable to load topic preferences.'
);

const topicPreferenceStatusMapFromResponse = (topicStatuses = []) => (
    topicStatuses.reduce((statusById, topicStatus) => {
        const topicId = typeof topicStatus?.topicId === 'string'
            ? topicStatus.topicId.trim()
            : '';
        const normalizedStatus = normalizeSubscribeStatus(topicStatus?.topicStatus);
        if (topicId && normalizedStatus !== null) {
            statusById[topicId] = normalizedStatus;
        }
        return statusById;
    }, {})
);

export function preloadTopicPreferences(options = {}) {
    return (dispatch, getState) => {
        const forceRefresh = options.forceRefresh === true;
        const userState = getState().user;
        const uid = userState.uid;

        if (!userState.authenticated || !uid) {
            return Promise.resolve(null);
        }
        if (userState.topicPreferencesLoading) {
            return Promise.resolve(userState.topicPreferenceStatusById);
        }
        if (userState.topicPreferencesLoaded && !forceRefresh) {
            return Promise.resolve(userState.topicPreferenceStatusById);
        }

        topicPreferenceRequestSequence += 1;
        const requestId = `topic-preferences-${topicPreferenceRequestSequence}`;
        const startedRevision = userState.topicPreferencesRevision || 0;

        dispatch({
            type: LOAD_TOPIC_PREFERENCES,
            payload: {
                uid,
                requestId,
                startedRevision,
            },
        });

        return validTokenExists()
            .then(() => axios.post('/user_topic_statuses', {}))
            .then((res) => {
                if (getState().user.uid !== uid) {
                    return null;
                }

                const statusById = topicPreferenceStatusMapFromResponse(
                    res.data?.topicStatuses || [],
                );
                dispatch({
                    type: TOPIC_PREFERENCES_LOADED,
                    payload: {
                        uid,
                        requestId,
                        startedRevision,
                        statusById,
                    },
                });
                return statusById;
            })
            .catch((err) => {
                if (getState().user.uid === uid) {
                    dispatch({
                        type: TOPIC_PREFERENCES_LOAD_FAILED,
                        payload: {
                            uid,
                            requestId,
                            error: topicPreferenceErrorMessage(err),
                        },
                    });
                }
                return null;
            });
    };
}

export function syncTopicPreferences(topicStatuses = [], options = {}) {
    return (dispatch, getState) => {
        const uid = options.uid || getState().user.uid;
        if (!uid) {
            return;
        }

        dispatch({
            type: SYNC_TOPIC_PREFERENCES,
            payload: {
                uid,
                replace: options.replace === true,
                topicStatuses,
            },
        });
    };
}

export function setTopicPreferenceStatus(topicId, topicStatus) {
    return (dispatch, getState) => {
        const userState = getState().user;
        const uid = userState.uid;
        const normalizedStatus = normalizeSubscribeStatus(topicStatus);
        const currentAccount = () => getState().user.uid === uid && getState().user.authenticated &&
            getState().user.forMeSessionRevision === userState.forMeSessionRevision;
        const previousHadStatus = Object.prototype.hasOwnProperty.call(
            userState.topicPreferenceStatusById,
            topicId,
        );
        const previousStatus = userState.topicPreferenceStatusById[topicId];

        if (!userState.authenticated || !uid || !topicId) {
            return Promise.reject(new Error('Unable to update topic preference.'));
        }

        if (userState.topicPreferencePendingMutationById?.[topicId]) {
            return Promise.reject(new Error('This topic preference is still saving.'));
        }
        const saveToken = beginForMePreferenceSave(dispatch, uid);
        let succeeded = false;
        topicPreferenceMutationSequence += 1;
        const mutationId = `topic-preference-mutation-${topicPreferenceMutationSequence}`;
        dispatch({
            type: TOPIC_PREFERENCE_MUTATION_STARTED,
            payload: {
                uid,
                mutationId,
                topicId,
                topicStatus: normalizedStatus,
            },
        });

        return validTokenExists()
            .then(() => {
                if (!currentAccount()) {
                    throw new Error('Your account changed. Please try again.');
                }
                return axios.post('/set_topic_status', {
                    topicId,
                    topicStatus: normalizedStatus,
                    deferFeedRefresh: true,
                });
            })
            .then((res) => {
                if (!currentAccount()) throw new Error('Your account changed. Please try again.');
                if (!res.data || res.data.error || res.data.success === false) {
                    throw new Error(res.data?.error || 'Unable to update topic preference.');
                }
                succeeded = true;
                dispatch({
                    type: TOPIC_PREFERENCE_MUTATION_FINISHED,
                    payload: {
                        uid,
                        mutationId,
                        topicId,
                    },
                });
                return res;
            })
            .catch((err) => {
                dispatch({
                    type: TOPIC_PREFERENCE_MUTATION_ROLLED_BACK,
                    payload: {
                        uid,
                        mutationId,
                        topicId,
                        previousHadStatus,
                        previousStatus,
                    },
                });
                return Promise.reject(err);
            }).finally(() => {
                finishForMePreferenceSave(dispatch, getState, uid, saveToken, succeeded,
                    normalizedStatus === true ? topicId : undefined);
            });
    };
}


// A new sign-in invalidates earlier login responses before Redux changes account.
let authSessionRevision = 0;
export const getAuthSessionRevision = () => authSessionRevision;

export const signinUser = (token, refreshToken, newUserDetails, options = {}) => (dispatch, getState) => {
    const revision = options.restoreRevision ?? ++authSessionRevision;
    const currentSession = () => revision === authSessionRevision;
    if (!currentSession()) return Promise.resolve({ success: false, cancelled: true });
    const savedHighlightStartedRevision = getState?.().savedHighlights?.revision || 0;
    const savedListStartedRevision = getState?.().user?.savedListRevision || 0;
    setFirebaseToken(token, refreshToken);
    const requestBody = {
        ...(newUserDetails || {}),
    };
    const onboarding = readOnboardingSession();
    const storedGuest = options.skipGuestMigration ? null : getPendingGuestData();
    let tokenUid = null;
    try {
        const claims = jwtDecode(token);
        tokenUid = claims.user_id || claims.sub || null;
    } catch {
        // The server validates the token; an unknown UID cannot claim owned picks.
    }
    const ownsSave = data => !data?.saveUid || data.saveUid === tokenUid;
    const pendingSave = !options.skipGuestMigration && onboarding?.savePending && !onboarding.guestMigrated ? onboarding : null;
    const candidateGuest = pendingSave || storedGuest;
    const pendingGuest = options.skipGuestMigration || !ownsSave(candidateGuest) ? null : candidateGuest;
    const explicitGuest = [pendingSave, storedGuest].find(data => data?.newId === requestBody.newId);
    if (explicitGuest && !ownsSave(explicitGuest)) {
        return Promise.resolve({ success: false, error: Object.assign(new Error('Use your original account to save these picks.'), { code: 'onboarding/account-changed' }) });
    }
    const pendingNewId = pendingGuest?.newId;
    const resumingSave = !requestBody.newId && pendingGuest;
    if (!requestBody.newId && pendingNewId) {
        requestBody.newId = pendingNewId;
        if (pendingSave) requestBody.onboardingPlayback = getOnboardingPlayback(pendingSave);
    }
    if (resumingSave && tokenUid && !pendingGuest.saveUid) {
        try {
            updateOnboardingSession({ savePending: true, saveUid: tokenUid }, { newId: pendingGuest.newId });
            if (storedGuest?.newId === pendingGuest.newId) {
                localStorage.setItem(GET_STARTED_NEW_USER_RESPONSE, JSON.stringify({ ...storedGuest, saveUid: tokenUid }));
            }
        } catch (error) {
            return Promise.resolve({ success: false, error });
        }
    }
    // Ordinary sign-in from Home can overlap a timed-out onboarding save.
    // Share its transport while each caller still validates its auth revision.
    const loginRequest = requestBody.newId
        ? runOnboardingSaveRequest(`login:${tokenUid || token}:${requestBody.newId}`, () => axios.post('/login', requestBody))
        : axios.post('/login', requestBody);
    return loginRequest
        .then((res) => {
            if (!currentSession()) return { success: false, cancelled: true };
            hydratePlayback(res.data.uid, res.data.trackPlaybackProgress);
            dispatch({ type: SET_AUTHENTICATED });
            dispatch({
                type: SET_USER,
                payload: res.data,
                meta: { savedHighlightStartedRevision, savedListStartedRevision },
            });
            dispatch(preloadTopicPreferences());
            if (resumingSave) {
                const eligibleIds = Array.isArray(res.data?.onboardingPreviewIds) ? new Set(res.data.onboardingPreviewIds) : null;
                const upcoming = getUpcomingOnboardingHighlights(pendingGuest.previews,
                    pendingGuest.sample?.id || pendingGuest.sampleId || pendingGuest.previews?.[0]?.id, pendingGuest.heardPreviewIds);
                saveOnboardingHomePicks({
                    uid: res.data.uid,
                    previews: eligibleIds ? upcoming.filter(preview => eligibleIds.has(preview.id)) : upcoming,
                    rosterVersion: pendingGuest.rosterVersion, selectedPairs: pendingGuest.selectedPairs,
                });
                if (readOnboardingSession()?.newId === pendingGuest.newId) markOnboardingSignedIn();
                try {
                    if (getPendingGuestData()?.newId === pendingGuest.newId) localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
                } catch {
                    // Successful migration stays successful if local cleanup fails.
                }
            }
            return {
                success: true,
                data: res.data,
            };
        })
        .catch((err) => {
            console.log(err);
            return {
                success: false,
                error: err,
            };
        });
};

export const signoutUser = () => (dispatch) => {
    authSessionRevision += 1;
    localStorage.removeItem('FirebaseIdToken');
    localStorage.removeItem('FirebaseRefreshToken');
    delete axios.defaults.headers.common['Authorization'];
    dispatch({ type: SET_UNAUTHENTICATED });
};


export const addSpeakerSubscribeIds = (speakerIds) => (dispatch) => {
    dispatch({
        type: ADD_SPEAKER_SUBSCRIBE_IDS,
        payload: {
            speakerIds: speakerIds
        }
    });
};


export const updateForMe = (forMePreviewsData) => (dispatch) => {
    dispatch({
        type: SET_FOR_ME,
        payload: forMePreviewsData,
    });
};

export const syncSubscribeStatuses = ({
    speakerSubscribeStatuses = [],
    podcastSubscribeStatuses = [],
    acknowledged = false,
}) => (dispatch) => {
    // Reads may be stale. Only confirmed writes advance the local edit ledger.
    if (acknowledged) {
        speakerSubscribeStatuses.forEach(payload => dispatch({ type: SET_SPEAKER_SUBSCRIBE_STATUS, payload }));
        podcastSubscribeStatuses.forEach(payload => dispatch({ type: SET_PODCAST_SUBSCRIBE_STATUS, payload }));
        return;
    }
    dispatch({
        type: SYNC_SUBSCRIBE_STATUSES,
        payload: {
            speakerSubscribeStatuses,
            podcastSubscribeStatuses,
        },
    });
};


export const loadRecentlyPlayed = () => (dispatch) => {
    dispatch({
        type: LOAD_RECENTLY_PLAYED,
        payload: null,
    });
    validTokenExists()
        .then(() => {
            axios
                .post('/recently_played', {})
                .then((res) => {
                    dispatch({
                        type: SET_RECENTLY_PLAYED,
                        payload: res.data,
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: SET_RECENTLY_PLAYED,
                        payload: null,
                    });
                    console.log(err)
                });
        })
        .catch((e) => {
            dispatch({
                type: SET_RECENTLY_PLAYED,
                payload: null,
            });
            console.log(e);
        });
}


export const loadRecentlySaved = () => (dispatch) => {
    dispatch({
        type: LOAD_RECENTLY_SAVED,
        payload: null,
    });
    validTokenExists()
        .then(() => {
            axios
                .post('/saved_tracks', {})
                .then((res) => {
                    dispatch({
                        type: SET_RECENTLY_SAVED,
                        payload: res.data,
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: SET_RECENTLY_SAVED,
                        payload: null,
                    });
                    console.log(err)
                });
        })
        .catch((e) => {
            dispatch({
                type: SET_RECENTLY_SAVED,
                payload: null,
            });
            console.log(e);
        });
}


export const forMeLoadingCountIncrease = () => (dispatch) => {
    dispatch({
        type: FOR_ME_LOADING_COUNT_INCREASE,
        payload: null,
    });
}


export const forMeLoadingCountDecrease = () => (dispatch) => {
    dispatch({
        type: FOR_ME_LOADING_COUNT_DECREASE,
        payload: null,
    });
}

// TODO remove unused function.
export const setGetStartedTopicsStatusTrue = () => (dispatch) => {
    dispatch({
        type: SET_GET_STARTED_TOPICS_STATUS_TRUE,
        payload: null,
    })
}

function getPendingGuestData() {
    const dataString = localStorage.getItem(GET_STARTED_NEW_USER_RESPONSE);
    if (!dataString) {
        return null;
    }
    // Older responses may contain only the guest ID and are still resumable.
    try {
        const data = JSON.parse(dataString);
        if (!data || !data.newId) {
            return null;
        }
        return data;
    } catch (e) {
        localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
        return null;
    }
}


export const invalidateLatestSubscribed = () => ({ type: INVALIDATE_LATEST_SUBSCRIBED });

export const loadLatestSubscribed = ({ force = false } = {}) => async (dispatch, getState) => {
    const {
        authenticated, uid, forMeSessionRevision, latestSubscribedLoading,
        latestSubscribedData, latestSubscribedFailedAt,
        speakerSubscribeIds, podcastSubscribeIds,
    } = getState().user;
    if (!authenticated || !uid || latestSubscribedLoading) return null;
    if (!force) {
        if (latestSubscribedData && typeof navigator !== 'undefined' && navigator.onLine === false) return null;
        const now = Date.now();
        if (latestSubscribedData?.setTime && now - latestSubscribedData.setTime < FOLLOWING_CACHE_TTL_MS) return null;
        if (latestSubscribedFailedAt && now - latestSubscribedFailedAt < FOLLOWING_RETRY_DELAY_MS) return null;
    }

    const requestId = ++latestSubscribedRequestSequence;
    const followIdentity = followingFeedIdentity(speakerSubscribeIds, podcastSubscribeIds);
    const currentRequest = () => {
        const user = getState().user;
        return user.authenticated && user.uid === uid &&
            user.forMeSessionRevision === forMeSessionRevision &&
            user.latestSubscribedRequestId === requestId;
    };
    dispatch({
        type: LOAD_LATEST_SUBSCRIED,
        payload: {
            requestId,
            reason: latestSubscribedData?.setTime === 0 ? 'preferences' : force ? 'manual' : 'automatic',
        },
    });
    try {
        await validTokenExists();
        if (!currentRequest()) return null;
        const { data } = await axios.post('/latest_subscriptions', {});
        if (!currentRequest()) return null;
        if (!data || data.error || !Array.isArray(data.trackInfos)) {
            throw new Error('Unable to refresh episodes.');
        }
        dispatch({
            type: SET_LATEST_SUBSCRIED,
            payload: { ...data, followIdentity },
        });
        return data;
    } catch (error) {
        if (!currentRequest()) return null;
        dispatch({
            type: LATEST_SUBSCRIBED_LOAD_FAILED,
            payload: { showError: force || !latestSubscribedData },
        });
        console.log(error);
        return null;
    }
}

export const setLastListen = (trackId, seconds) => (dispatch) => {
    if (seconds < 10) {
        return;
    }
    validTokenExists()
        .then(() => {
            axios
                .post('/last_listen', {
                    trackId: trackId,
                    seconds: seconds,
                })
                .then((res) => {
                    if (res.data.error) {
                        // TODO display error message to user...
                        console.log(res.data.error);
                    }
                })
                .catch((err) => {
                    // TODO display error message to user...
                    console.log(err.response.data.error);
                });
        })
        .catch((e) => {
            console.log(e);
        });
};


const subscribeStatusErrorMessage = (err) => (
    err?.response?.data?.error || err?.message || 'Something went wrong, please try again.'
);

const alertSubscribeStatusError = (prefix, errorMessage) => {
    console.log(errorMessage);
    if (typeof window !== 'undefined' && window.alert) {
        window.alert(`${prefix}: ${errorMessage}`);
    }
};

export const saveShowSubscribeStatus = (show, subscribeStatus) => (dispatch, getState) => {
    const { uid, forMeSessionRevision } = getState().user;
    const requestKey = `${uid}:${showSearchKey(show)}`;
    const saveToken = beginForMePreferenceSave(dispatch, uid);
    let succeeded = false;
    const currentAccount = () => getState().user.uid === uid && getState().user.authenticated &&
        getState().user.forMeSessionRevision === forMeSessionRevision;
    // Serialize requests across navigation too: an older detail-page save must
    // finish before a newer preference from search can be committed.
    const previous = showPreferenceRequests.get(requestKey) || Promise.resolve();
    const request = previous.catch(() => {}).then(async () => {
        const result = await saveShowStatus(show, subscribeStatus, currentAccount);
        if (!currentAccount()) throw new Error('Your account changed. Please try again.');
        dispatch({
            type: SET_PODCAST_SUBSCRIBE_STATUS,
            payload: { subscribeId: result.subscribeId, subscribeStatus: result.subscribeStatus },
        });
        dispatch(invalidateLatestSubscribed());
        succeeded = true;
        return result;
    }).finally(() => {
        finishForMePreferenceSave(dispatch, getState, uid, saveToken, succeeded);
        if (showPreferenceRequests.get(requestKey) === request) showPreferenceRequests.delete(requestKey);
    });
    showPreferenceRequests.set(requestKey, request);
    return request;
};

export const setPodcastSubscribeStatus = (subscribeId, subscribeStatus) => (dispatch, getState) => {
    const { uid, forMeSessionRevision } = getState().user;
    return saveShowSubscribeStatus(
        { source: { id: subscribeId } }, normalizeSubscribeStatus(subscribeStatus),
    )(dispatch, getState)
        .catch((error) => {
            if (getState().user.uid === uid && getState().user.authenticated &&
                getState().user.forMeSessionRevision === forMeSessionRevision) {
                alertSubscribeStatusError('Unable to save show', subscribeStatusErrorMessage(error));
            }
        });
};


export const setSpeakerSubscribeStatus = (subscribeId, subscribeStatus, { suppressErrorAlert = false } = {}) => (dispatch, getState) => {
    const { uid, forMeSessionRevision } = getState().user;
    const status = normalizeSubscribeStatus(subscribeStatus);
    const currentAccount = () => getState().user.uid === uid && getState().user.authenticated &&
        getState().user.forMeSessionRevision === forMeSessionRevision;
    if (!currentAccount() || !subscribeId) return Promise.resolve(null);
    const key = `${uid}:${subscribeId}`;
    const saveToken = beginForMePreferenceSave(dispatch, uid);
    let succeeded = false;
    const previous = speakerPreferenceRequests.get(key) || Promise.resolve();
    const request = previous.catch(() => {}).then(async () => {
        await validTokenExists();
        if (!currentAccount()) return null;
        const { data } = await axios.post('/subscribe', {
            subscribeId, subscribeStatus: status, isSpeaker: true,
        });
        if (!currentAccount()) return null;
        if (!data || data.error || data.success === false) throw new Error(data?.error || 'Unable to save speaker.');
        dispatch({ type: SET_SPEAKER_SUBSCRIBE_STATUS, payload: { subscribeId, subscribeStatus: status } });
        dispatch(invalidateLatestSubscribed());
        succeeded = true;
        return data;
    }).catch((error) => {
        if (currentAccount() && !suppressErrorAlert) alertSubscribeStatusError('Unable to save speaker', subscribeStatusErrorMessage(error));
        return null;
    }).finally(() => {
        finishForMePreferenceSave(dispatch, getState, uid, saveToken, succeeded);
        if (speakerPreferenceRequests.get(key) === request) speakerPreferenceRequests.delete(key);
    });
    speakerPreferenceRequests.set(key, request);
    return request;
};


export const addTrackToSavedList = (trackId, emoji, optListId) => (dispatch) => {
    return validTokenExists()
        .then(() => {
            if (optListId) {
                // Update local data to reflect changes.
                dispatch({
                    type: ADD_TRACK_TO_SAVED_LIST,
                    payload: {
                        trackId: trackId,
                        listId: optListId,
                    }
                });
                dispatch({
                    type: UPDATED_SAVED_LIST,
                    payload: {
                        name: emoji,
                        id: optListId,
                    }
                });
            }
            return axios
                .post('/saved_lists_add_track', {
                    name: emoji,
                    trackId: trackId,
                })
                .then((res) => {
                    if (res.data.success) {
                        const responseListId = res.data.listId;
                        if (!optListId) {
                            dispatch({
                                type: ADD_TRACK_TO_SAVED_LIST,
                                payload: {
                                    trackId: trackId,
                                    listId: responseListId,
                                }
                            });
                            dispatch({
                                type: UPDATED_SAVED_LIST,
                                payload: {
                                    name: emoji,
                                    id: responseListId,
                                }
                            });
                        }
                        return true;
                    }
                    if (res.data.error) {
                        // TODO display error message to user...
                        console.log(res.data.error);
                        window.alert('Error: ' + res.data.error);
                        if (optListId) {
                            // Revert changes to local data.
                            dispatch({
                                type: REMOVE_TRACK_FROM_SAVED_LIST,
                                payload: {
                                    trackId: trackId,
                                    listId: optListId,
                                }
                            });
                        }
                    }
                    return false;
                })
                .catch((err) => {
                    // TODO display error message to user...
                    console.log(err.response.data.error);
                    window.alert('Unexpected Error: ' + err.response.data.error);
                    if (optListId) {
                        // Revert changes to local data.
                        dispatch({
                            type: REMOVE_TRACK_FROM_SAVED_LIST,
                            payload: {
                                trackId: trackId,
                                listId: optListId,
                            }
                        });
                    }
                    return false;
                });
        })
        .catch((e) => {
            console.log(e);
            return false;
        });
}


export const removeTrackFromSavedList = (trackId, emoji, listId) => (dispatch) => {
    return validTokenExists()
        .then(() => {
            dispatch({
                type: UPDATED_SAVED_LIST,
                payload: {
                    name: emoji,
                    id: listId,
                }
            });
            dispatch({
                type: REMOVE_TRACK_FROM_SAVED_LIST,
                payload: {
                    trackId: trackId,
                    listId: listId,
                }
            });
            return axios
                .post('/saved_lists_delete_track', {
                    trackId: trackId,
                    listId: listId,
                })
                .then((res) => {
                    if (res.data.success) {
                        return true;
                    }
                    if (res.data.error) {
                        // TODO display error message to user...
                        console.log(res.data.error);
                        window.alert('Error: ' + res.data.error);
                        // Revert delete.
                        dispatch({
                            type: ADD_TRACK_TO_SAVED_LIST,
                            payload: {
                                trackId: trackId,
                                listId: listId,
                            }
                        });
                    }
                    return false;
                })
                .catch((err) => {
                    // TODO display error message to user...
                    console.log(err.response.data.error);
                    window.alert('Unexpected Error: ' + err.response.data.error);
                    // Revert delete.
                    dispatch({
                        type: ADD_TRACK_TO_SAVED_LIST,
                        payload: {
                            trackId: trackId,
                            listId: listId,
                        }
                    });
                    return false;
                });
        })
        .catch((e) => {
            console.log(e);
            return false;
        });
}

export const addSavedList = (name, id) => (dispatch) => {
    dispatch({
        type: UPDATED_SAVED_LIST,
        payload: {
            name: name,
            id: id,
        }
    });
}

export const deleteSavedList = (id) => (dispatch) => {
    dispatch({
        type: DELETED_SAVED_LIST,
        payload: {
            id: id,
        }
    });
}


export const saveTrack = (trackId, saveStatus) => (dispatch, getState) => {
    const { uid, forMeSessionRevision } = getState().user;
    const currentAccount = () => {
        const user = getState().user;
        return user.authenticated && user.uid === uid &&
            user.forMeSessionRevision === forMeSessionRevision;
    };
    if (!currentAccount()) return Promise.resolve();

    dispatch({ type: SAVE_TRACK_MUTATION_STARTED });
    const key = JSON.stringify([uid, forMeSessionRevision, trackId]);
    const previous = savedTrackRequests.get(key) || Promise.resolve();
    // Keep save/remove ordering and take each rollback snapshot after the prior request settles.
    const request = previous.catch(() => {}).then(async () => {
        let previousListIds;
        let optimisticListIds;
        let updated = false;
        try {
            if (!currentAccount()) return;
            await validTokenExists();
            if (!currentAccount()) return;
            previousListIds = getState().user.savedTrackIdToSavedListIds[trackId];
            dispatch({
                type: SET_SAVE_TRACK_STATUS,
                payload: { trackId, saveStatus, savedListIds: previousListIds },
            });
            optimisticListIds = getState().user.savedTrackIdToSavedListIds[trackId];
            updated = true;
            const { data } = await axios.post('/save_track', { trackId, saveStatus });
            if (!data || data.error || data.general || data.success === false) {
                throw new Error(data?.error || data?.general || 'Unable to update saved episode.');
            }
        } catch (error) {
            console.log(error);
            if (!currentAccount()) return;
            // A list edit can independently save the episode while this request is pending.
            if (updated && getState().user.savedTrackIdToSavedListIds[trackId] === optimisticListIds) {
                dispatch({
                    type: SET_SAVE_TRACK_STATUS,
                    payload: { trackId, saveStatus: previousListIds !== undefined, savedListIds: previousListIds },
                });
            }
            window.alert(saveStatus
                ? 'Couldn’t save this episode. Try again.'
                : 'Couldn’t remove this episode from saved. Try again.');
        }
    }).finally(() => {
        if (currentAccount()) dispatch({ type: SAVE_TRACK_MUTATION_FINISHED });
        if (savedTrackRequests.get(key) === request) savedTrackRequests.delete(key);
    });
    savedTrackRequests.set(key, request);
    return request;
};
